import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  PROJECT_STATUS_ORDER,
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_BORDER,
  PROJECT_STATUS_RING,
  type ProjectKpiStatus,
} from '@/constants/projectStatusLabels';
import { useProjectStatusStats, type BuildStageKey, type TenderStageKey } from '@/hooks/projects/useProjectStatusStats';
import type { ProjectFiltersData } from '@/hooks/useOptimizedProjectsQuery';
import {
  LIFECYCLE_STAGE_BUILD_COLORS,
  LIFECYCLE_STAGE_TENDER_COLORS,
  type LifecycleStageBuild,
  type LifecycleStageTender,
} from '@/constants/lifecycleStages';

const gfaFmt = new Intl.NumberFormat('vi-VN');
const formatGfa = (n: number) => (n > 0 ? `${gfaFmt.format(Math.round(n))} m²` : '—');

interface Props {
  activeStatus: ProjectKpiStatus | null;
  onStatusToggle: (status: ProjectKpiStatus) => void;
  activeBuildStage: BuildStageKey | null;
  onBuildStageToggle: (stage: BuildStageKey) => void;
  activeTenderStage: TenderStageKey | null;
  onTenderStageToggle: (stage: TenderStageKey) => void;
  filters?: ProjectFiltersData;
}

const BUILD_CHIPS: Array<{ key: BuildStageKey; label: string }> = [
  { key: 'unset', label: 'Chưa set' },
  { key: 'm1_open', label: 'B1 Mở CT' },
  { key: 'm2_structural', label: 'B2 Phần Thô' },
  { key: 'm3_mep', label: 'B3 M&E' },
  { key: 'm4_finishing', label: 'B4 Hoàn thiện' },
  { key: 'm5_acceptance', label: 'B5 Nghiệm thu' },
];

const TENDER_CHIPS: Array<{ key: TenderStageKey; label: string }> = [
  { key: 'unset', label: 'Chưa set' },
  { key: 't1_orientation', label: 'T1 Định hướng' },
  { key: 't2_submitted', label: 'T2 Đã submit' },
  { key: 't3_negotiation', label: 'T3 Thương lượng' },
];

const UNSET_ACTIVE_CLASS =
  'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-900/30 dark:text-gray-300 dark:border-gray-700';

export default function ProjectStatusPanel({
  activeStatus,
  onStatusToggle,
  activeBuildStage,
  onBuildStageToggle,
  activeTenderStage,
  onTenderStageToggle,
  filters,
}: Props) {
  const { data, isLoading } = useProjectStatusStats(filters);


  if (isLoading && !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {PROJECT_STATUS_ORDER.map((s) => (
          <Card key={s} className={`border-l-4 ${PROJECT_STATUS_BORDER[s]}`}>
            <CardContent className="p-4 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-3 w-20" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {PROJECT_STATUS_ORDER.map((status) => {
        const stat = data?.[status] ?? { count: 0, total_gfa: 0 };
        const isActive = activeStatus === status;
        const isBuild = status === 'build';
        const isTender = status === 'tender';
        let displayCount = stat.count;
        let displayGfa = stat.total_gfa;
        if (isBuild && activeBuildStage && stat.build_stages) {
          displayCount = stat.build_stages[activeBuildStage].count;
          displayGfa = stat.build_stages[activeBuildStage].total_gfa;
        } else if (isTender && activeTenderStage && stat.tender_stages) {
          displayCount = stat.tender_stages[activeTenderStage].count;
          displayGfa = stat.tender_stages[activeTenderStage].total_gfa;
        }
        return (
          <Card
            key={status}
            role="button"
            tabIndex={0}
            aria-pressed={isActive}
            onClick={() => onStatusToggle(status)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onStatusToggle(status);
              }
            }}
            className={cn(
              'border-l-4 cursor-pointer transition-colors select-none',
              'hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              PROJECT_STATUS_BORDER[status],
              isActive && `ring-2 ring-offset-1 ${PROJECT_STATUS_RING[status]} bg-muted/30`
            )}
          >
            <CardContent className="p-4">
              <div className="text-sm font-medium text-muted-foreground">
                {PROJECT_STATUS_LABELS[status]}
              </div>
              <div className="text-2xl font-bold mt-1">{displayCount} dự án</div>
              <div className="text-xs text-muted-foreground mt-1">
                {formatGfa(displayGfa)}
              </div>

              {status === 'build' && (
                <div
                  className="flex flex-wrap gap-1 mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {BUILD_CHIPS.map((chip) => {
                    const count = stat.build_stages?.[chip.key].count ?? 0;
                    const isChipActive = activeBuildStage === chip.key;
                    const activeColor =
                      chip.key === 'unset'
                        ? UNSET_ACTIVE_CLASS
                        : LIFECYCLE_STAGE_BUILD_COLORS[chip.key as LifecycleStageBuild];
                    return (
                      <button
                        key={chip.key}
                        type="button"
                        aria-pressed={isChipActive}
                        onClick={(e) => {
                          e.stopPropagation();
                          onBuildStageToggle(chip.key);
                        }}
                        className={cn(
                          'h-6 px-1.5 text-[10px] font-semibold rounded-md border transition-colors',
                          isChipActive
                            ? activeColor
                            : 'border-border text-muted-foreground hover:bg-muted/60'
                        )}
                      >
                        {chip.label} · {count}
                      </button>
                    );
                  })}
                </div>
              )}

              {status === 'tender' && (
                <div
                  className="flex flex-wrap gap-1 mt-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {TENDER_CHIPS.map((chip) => {
                    const count = stat.tender_stages?.[chip.key].count ?? 0;
                    const isChipActive = activeTenderStage === chip.key;
                    const activeColor =
                      chip.key === 'unset'
                        ? UNSET_ACTIVE_CLASS
                        : LIFECYCLE_STAGE_TENDER_COLORS[chip.key as LifecycleStageTender];
                    return (
                      <button
                        key={chip.key}
                        type="button"
                        aria-pressed={isChipActive}
                        onClick={(e) => {
                          e.stopPropagation();
                          onTenderStageToggle(chip.key);
                        }}
                        className={cn(
                          'h-6 px-1.5 text-[10px] font-semibold rounded-md border transition-colors',
                          isChipActive
                            ? activeColor
                            : 'border-border text-muted-foreground hover:bg-muted/60'
                        )}
                      >
                        {chip.label} · {count}
                      </button>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
